import { useRef, useState, type ReactNode, type RefObject } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui';
import { ContactForm } from '~/components/common/ContactForm';
import type { SavedContact } from '~/utils/contact';

type AddSellerContactParts = Readonly<{
  trigger: ReactNode;
  form: ReactNode;
}>;

type AddSellerContactPanelProps = Readonly<{
  organizationId: string;
  testId: string;
  disabled?: boolean;
  initiallyOpen?: boolean;
  focusAfterSave?: RefObject<HTMLElement | null>;
  onAdded: (contact: SavedContact) => void;
  onCancel?: () => void;
  children: (parts: AddSellerContactParts) => ReactNode;
}>;

/**
 * Adds a seller contact in place. `children` places the Add contact button and the form,
 * which can sit apart, such as a button in a row header and the form below it.
 *
 * @example
 *   <AddSellerContactPanel
 *     organizationId={organization.id}
 *     testId="seller-row-add-contact"
 *     onAdded={(contact) => select(contact)}
 *   >
 *     {({ trigger, form }) => (
 *       <>
 *         {trigger}
 *         {form}
 *       </>
 *     )}
 *   </AddSellerContactPanel>
 */
export function AddSellerContactPanel({
  organizationId,
  testId,
  disabled = false,
  initiallyOpen = false,
  focusAfterSave,
  onAdded,
  onCancel,
  children,
}: AddSellerContactPanelProps) {
  const { t } = useTranslation('general');
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = (focusTarget: RefObject<HTMLElement | null>) => {
    flushSync(() => setIsOpen(false));
    if (focusTarget.current) {
      focusTarget.current.focus();
    }
  };

  const handleCancel = () => {
    close(triggerRef);
    if (onCancel) {
      onCancel();
    }
  };

  const handleSaved = (contact: SavedContact) => {
    onAdded(contact);
    close(focusAfterSave ?? triggerRef);
  };

  return children({
    trigger: isOpen ? null : (
      <Button
        ref={triggerRef}
        variant="secondary"
        size="sm"
        width="auto"
        className="shrink-0 whitespace-nowrap"
        onClick={() => setIsOpen(true)}
        disabled={disabled}
        data-testid={testId}
      >
        {t('general.add_contact')}
      </Button>
    ),
    form: isOpen ? (
      <ContactForm
        organizationId={organizationId}
        audience="seller"
        onCancel={handleCancel}
        onSaved={handleSaved}
      />
    ) : null,
  });
}
